/**
 * Building factory - creates building entities with all required components
 */

import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import { createPosition } from '@/game/components/Position';
import { createHealth } from '@/game/components/Health';
import { createFaction } from '@/game/components/Faction';
import { createBuilding } from '@/game/components/Building';
import { createSpawner } from '@/game/components/Spawner';
import { createRender } from '@/game/components/Render';
import { createCollider, getBuildingCollisionRadius } from '@/game/components/Collider';
import { createCombat } from '@/game/components/Combat';
import { BUILDING_STATS } from '@/game/factions/FactionData';

// Building defense values based on type
const BUILDING_DEFENSE: Record<string, number> = {
  capital: 20,    // Very sturdy
  barracks: 8,
  archery: 6,
  spearcamp: 8,
  stable: 6,
  smithy: 10,     // Heavy construction
  temple: 5,
  tower: 15,      // Defensive structure
  farm: 3,        // Weak
  mine: 5,
  market: 4,
  wall: 12,
};

// Tower combat stats (attack, range, attackSpeed)
const TOWER_COMBAT_STATS = {
  attack: 25,       // Slightly more than archer
  range: 6.0,       // Slightly more than archer
  attackSpeed: 1.2, // Slightly slower than archer
};
import type { BuildingType, FactionId, Entity, UnitType } from '@/types';

export function createBuildingEntity(
  world: World,
  buildingType: BuildingType,
  factionId: FactionId,
  x: number,
  y: number,
  z: number,
  constructed: boolean = true
): Entity {
  const entity = world.createEntity();
  const stats = BUILDING_STATS[buildingType];

  world.addComponent(entity, ComponentTypes.Position, createPosition(x, y, z));
  world.addComponent(entity, ComponentTypes.Health, createHealth(stats.health));
  world.addComponent(entity, ComponentTypes.Faction, createFaction(factionId));
  world.addComponent(entity, ComponentTypes.Building, createBuilding(buildingType, constructed));
  world.addComponent(entity, ComponentTypes.Render, createRender());

  // Add combat component for buildings
  // Towers can attack, other buildings only have defense
  const defense = BUILDING_DEFENSE[buildingType] || 5;
  if (buildingType === 'tower') {
    world.addComponent(entity, ComponentTypes.Combat, createCombat(
      TOWER_COMBAT_STATS.attack,
      defense,
      TOWER_COMBAT_STATS.range,
      TOWER_COMBAT_STATS.attackSpeed
    ));
  } else {
    world.addComponent(entity, ComponentTypes.Combat, createCombat(0, defense, 0, 0));
  }

  // Add collider for collision detection (buildings are static - cannot be pushed)
  const collisionRadius = getBuildingCollisionRadius(buildingType);
  world.addComponent(entity, ComponentTypes.Collider, createCollider(collisionRadius, true, Infinity));

  // Add spawner if building can spawn units
  if (stats.spawnType && stats.spawnInterval) {
    world.addComponent(
      entity,
      ComponentTypes.Spawner,
      createSpawner(stats.spawnType as UnitType, stats.spawnInterval)
    );
  }

  return entity;
}
